import { persist } from "zustand/middleware";
import { createWithEqualityFn } from "zustand/traditional";

export enum ColorTheme {
  Light = "light",
  Dark = "dark",
  // Follows the device (prefers-color-scheme), live.
  System = "system",
}

const DARK_QUERY = "(prefers-color-scheme: dark)";

const systemPrefersDark = (): boolean =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia(DARK_QUERY).matches;

// Light or Dark: the theme the screen shows for a setting.
export const resolveTheme = (
  theme: ColorTheme,
  prefersDark = systemPrefersDark(),
): ColorTheme.Light | ColorTheme.Dark =>
  theme === ColorTheme.Dark || (theme === ColorTheme.System && prefersDark)
    ? ColorTheme.Dark
    : ColorTheme.Light;

const applyTheme = (theme: ColorTheme): void => {
  if (typeof document === "undefined") return;
  document.documentElement.classList.toggle(
    ColorTheme.Dark.toString(),
    resolveTheme(theme) === ColorTheme.Dark,
  );
};

interface AppSettingsState {
  theme: ColorTheme;
  inputLanguage: string;
  toggleTheme: () => void;
  setTheme: (theme: ColorTheme) => void;
  setInputLanguage: (language: string) => void;
}

export const useAppSettingsStore = createWithEqualityFn<AppSettingsState>()(
  persist(
    (set, get) => ({
      theme: ColorTheme.Light,
      inputLanguage: "eng",
      // Light <-> Dark from what is shown now (System counts as what the
      // device shows).
      toggleTheme: (): void => {
        const newTheme =
          resolveTheme(get().theme) === ColorTheme.Dark
            ? ColorTheme.Light
            : ColorTheme.Dark;
        applyTheme(newTheme);
        set({ theme: newTheme });
      },
      setTheme: (theme: ColorTheme): void => {
        applyTheme(theme);
        set({ theme });
      },
      setInputLanguage: (language: string): void => {
        set({ inputLanguage: language });
      },
    }),
    {
      name: "app-settings",
      onRehydrateStorage:
        () =>
        (state): void => {
          if (state) applyTheme(state.theme);
        },
    },
  ),
);

// System: follow the device when it switches while the app is open.
if (typeof window !== "undefined" && typeof window.matchMedia === "function") {
  window.matchMedia(DARK_QUERY).addEventListener?.("change", () => {
    const { theme } = useAppSettingsStore.getState();
    if (theme === ColorTheme.System) applyTheme(theme);
  });
}
